<template>
  <div class="noticia-card transform-container">
    <router-link v-if="props.noticia.id" :to="destinoNoticia" class="noticia-link">
      <div class="noticia-imagem">
        <img :src="props.noticia.imagemUrl" :alt="props.noticia.titulo" />
      </div>
      <div class="noticia-texto">
        <span class="titulo">
          <span v-if="props.noticia.acaoSigla" class="sigla-inline-badge" :style="getCorSigla()">
            {{ props.noticia.acaoSigla.toUpperCase() }}
          </span>
          <span v-if="props.noticia.acaoSigla"> - </span>
          <span class="titulo-texto"> {{ props.noticia.titulo }}</span>
        </span>
        <span class="data-publicacao">
          {{ formatarData(props.noticia.dataPublicacao) }}
        </span>
        <span class="info">{{ extrairTextoHtml(props.noticia.texto) }}</span>
        <div v-if="props.noticia.palavrasChaves && props.noticia.palavrasChaves.length" class="palavras-chave">
          <span v-for="palavra in props.noticia.palavrasChaves" :key="palavra" class="tag">
            {{ palavra }}
          </span>
        </div>
      </div>
    </router-link>

    <div class="botoes" @click.stop v-if="checkEdicao()">
      <button type="button" @click.stop="abrirEdicao()" class="btn-card btn-editar-card editar">
        <i class="ti ti-edit"></i>
        Editar
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import acaoService from "@/service/acaoService";
import noticiaService from "@/service/noticiaService";
import { formatarData, extrairTextoHtml } from "@/utils/FormatarEntradas";

const props = defineProps({
  noticia: {
    type: Object,
    required: true,
  },
  isCoordenador: {
    type: Boolean,
    default: null,
  },
});

const router = useRouter();
const route = useRoute();
const isCoordenadorLocal = ref(false);

const ehCoordenador = computed(() => {
  if (props.isCoordenador !== null && props.isCoordenador !== undefined) {
    return props.isCoordenador;
  }
  return isCoordenadorLocal.value;
});

const destinoNoticia = computed(() => {
  const acaoId = route.params.id;
  if (acaoId) {
    return `/acao/${acaoId}/noticias/${props.noticia.id}`;
  }
  return `/home-noticia/${props.noticia.id}`;
});

function checkEdicao() {
  const auth = useAuthStore();
  return (
    auth.hasRole("ADMINISTRADOR") || ehCoordenador.value
  );
}

async function checarPermissaoCoordenador() {
  if (props.isCoordenador !== null && props.isCoordenador !== undefined) {
    return;
  }
  const acaoUrl = props.noticia?.acaoUrl?.split("/") || [];
  const targetAcaoId =
    route.params.id ||
    props.noticia?.acaoId ||
    props.noticia?.acao?.id ||
    acaoUrl[acaoUrl.length - 1];

  if (targetAcaoId) {
    try {
      isCoordenadorLocal.value =
        await acaoService.verificarCoordenador(targetAcaoId);
    } catch {
      isCoordenadorLocal.value = false;
    }
  }
}

watch(
  () => props.isCoordenador,
  (novoValor) => {
    if (novoValor === null || novoValor === undefined) {
      checarPermissaoCoordenador();
    }
  }
);

async function abrirEdicao() {
  const acaoUrl = props.noticia?.acaoUrl?.split("/") || [];
  let acaoId =
    route.params.id ||
    props.noticia?.acaoId ||
    props.noticia?.acao?.id ||
    acaoUrl[acaoUrl.length - 1];

  if (!acaoId && props.noticia?.id) {
    try {
      const detalhe = await noticiaService.getNoticiaById(props.noticia.id);
      acaoId = detalhe?.acaoId || detalhe?.acao?.id;
    } catch (e) {
      console.error("Erro ao obter acaoId da notícia:", e);
    }
  }

  if (props.noticia?.id) {
    router.push({
      name: "editar-noticia",
      params: { id: acaoId || 0, noticiaId: props.noticia.id },
    });
  }
}

function getCorSigla() {
  return {
    backgroundColor: props.noticia.acaoCorSigla || "#48298a",
    borderColor: props.noticia.acaoCorSigla || "#48298a",
  };
}

onMounted(() => {
  checarPermissaoCoordenador();
});
</script>

<style scoped>
.noticia-card {
  position: relative;
  background-color: var(--branco);
  border: 1px solid hsla(0, 0%, 0%, 0.3);
  border-radius: 0.4rem;
  overflow: hidden;
  transition: transform 200ms;
}

.noticia-card:hover {
  transform: translateY(-2px);
  border-color: var(--roxo-claro);
  box-shadow:
    0 10px 24px -3px hsla(258, 53%, 28%, 0.14),
    0 4px 10px -2px hsla(258, 53%, 28%, 0.06);
}

.noticia-link {
  display: flex;
  align-items: flex-start;
  padding: 15px;
  text-decoration: none;
  color: inherit;
  gap: 16px;
  box-sizing: border-box;
}

.noticia-imagem {
  flex-shrink: 0;
  width: 250px;
  height: 150px;
  border-radius: 6px;
  overflow: hidden;
}

.noticia-imagem img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.noticia-texto {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  flex: 1;
  min-width: 0;
  /* Previne vazamento do flex container */
}

.sigla-inline-badge {
  display: inline-block;
  padding: 2px 6px;
  font-size: 0.8rem;
  font-weight: 700;
  border-radius: 4px;
  margin-right: 4px;
  vertical-align: middle;
  border: 1px solid transparent;
  line-height: 1.2;
  color: var(--branco);
}

.noticia-texto .titulo {
  font-size: 1.375rem;
  font-weight: 600;
  color: var(--cinza-escuro);
  line-height: 1.3;

  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.data-publicacao {
  font-size: 0.82rem;
  color: var(--cinza-medio);
  font-weight: 500;
}

.info {
  font-size: 0.95rem;
  color: var(--cinza-escuro);
  margin-left: 0;
  line-height: 1.4;

  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.palavras-chave {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.4rem;
}

.tag {
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.65rem;
  border-radius: 999px;
  background-color: var(--roxo-superficie);
  color: var(--roxo-primario);
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
}

.botoes {
  position: absolute;
  bottom: 10px;
  right: 10px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  z-index: 2;
}

.btn-card {
  font-size: 0.82rem;
  font-weight: 600;
  padding: 5px 14px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 1px 3px hsla(0, 0%, 0%, 0.08);
  user-select: none;
  line-height: 1.4;
}

.btn-editar-card {
  background-color: var(--roxo-primario);
  color: var(--branco);
}

.btn-editar-card:hover {
  background-color: var(--roxo-medio);
}

@media (max-width: 768px) {
  .noticia-imagem {
    width: 200px;
    height: 140px;
  }

  .noticia-texto .titulo {
    font-size: 1.2rem;
  }
}

@media (max-width: 600px) {
  .noticia-link {
    flex-direction: column;
    gap: 12px;
    padding: 12px;
    padding-bottom: 48px;

  }


  .noticia-imagem {
    width: 100%;
    height: 180px;
  }

  .noticia-imagem img {
    border-radius: 4px;
  }

  .noticia-texto {
    width: 100%;
  }

  .noticia-texto .titulo {
    font-size: 1.1rem;
    -webkit-line-clamp: 3;
  }

  .info {
    font-size: 0.9rem;
    -webkit-line-clamp: 3;
  }

  .botoes {
    bottom: 12px;
    right: 12px;
  }

}
</style>